import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Camera, Flower2, Sparkles, ArrowRight, Leaf } from "lucide-react";
import api from "@/lib/api";
import { eur, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import BackNav from "@/components/BackNav";

export default function SingolaUscita() {
  const [config, setConfig] = useState(null);
  const { user } = useAuth();
  const [params] = useSearchParams();
  const deep = ["profonda", "specialistica"].includes(params.get("pulizia"));

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config) return <Loading />;

  const cta = user ? `/checkout?type=singola${deep ? "&pulizia=specialistica" : ""}` : "/accedi";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-12">
      <BackNav />
      <div className="max-w-2xl">
        <p className="eyebrow mb-2">Singola uscita</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-4">Un intervento, quando serve.</h1>
        <p className="text-body leading-relaxed">
          Perfetta per provare il servizio o per un'occasione speciale: un anniversario, una ricorrenza,
          una visita che non riesci a fare di persona.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        <div className="card-soft p-7 border-2 !border-[#4a7c59]" data-testid="trial-price-card">
          <p className="eyebrow mb-1">Prima uscita di prova</p>
          <p className="font-serif text-5xl text-navy mb-3">{eur(config.trial_price)}</p>
          <p className="text-sm text-body leading-relaxed">
            Comprende 1 intervento singolo con mazzo di margherite, consegna e intervento presso il luogo indicato.
            La prova è una sola per cliente: se scegli un abbonamento entro 10 giorni, i {eur(config.trial_price)}
            ti verranno riconosciuti come credito sul primo pagamento.
          </p>
        </div>
        <div className="card-soft p-7" data-testid="second-price-card">
          <p className="eyebrow mb-1">Dalla seconda uscita in poi</p>
          <p className="font-serif text-5xl text-navy mb-3">{eur(config.single_visit_price)}</p>
          <p className="text-sm text-body leading-relaxed">
            Il prezzo resta sempre lo stesso: {eur(config.single_visit_price)} a uscita, con fotografia finale inclusa.
            Puoi aggiungere fiori speciali o la pulizia approfondita come supplementi separati.
          </p>
        </div>
        <div className="card-ivory p-7" data-testid="credit-example-card">
          <p className="eyebrow mb-1">Esempio con abbonamento</p>
          <div className="text-sm text-navy space-y-2 mt-3">
            <div className="flex justify-between"><span>Abbonamento</span><span className="font-semibold">{eur(129)}</span></div>
            <div className="flex justify-between"><span>Credito prima uscita</span><span className="font-semibold text-[#4a7c59]">−{eur(config.trial_price)}</span></div>
            <div className="border-t border-[#ece7dd] pt-2 flex justify-between"><span>Totale da pagare</span><span className="font-serif text-2xl">{eur(129 - config.trial_price)}</span></div>
            <p className="text-xs text-muted-soft pt-1">Il credito scatta se attivi l'abbonamento entro 10 giorni dalla prima uscita.</p>
          </div>
        </div>
      </div>

      <div className="card-soft p-8">
        <p className="eyebrow mb-4">Cosa comprende ogni uscita</p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          <div className="flex gap-3">
            <Flower2 className="text-[#4a7c59] shrink-0" size={22} />
            <div>
              <p className="font-semibold text-navy mb-1">Fiori freschi</p>
              <p className="text-body">Margherite standard incluse, oppure una proposta dal catalogo con un piccolo supplemento.</p>
            </div>
          </div>
          <div className="flex gap-3" data-testid="cura-ordinaria-item">
            <Leaf className="text-[#4a7c59] shrink-0" size={22} />
            <div>
              <p className="font-semibold text-navy mb-1">Cura ordinaria <span className="text-[#4a7c59]">(sempre inclusa)</span></p>
              <p className="text-body">Pulizia di base del luogo: rimozione di erbacce, foglie e piccoli detriti, con riordino generale dell'area.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Sparkles className="text-[#4a7c59] shrink-0" size={22} />
            <div>
              <p className="font-semibold text-navy mb-1">Pulizia approfondita (opzionale, +{eur(config.cleaning_ordinary_price)})</p>
              <p className="text-body">Pulizia accurata di lapide, statue e lumi, con prodotti professionali specifici.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Camera className="text-[#4a7c59] shrink-0" size={22} />
            <div>
              <p className="font-semibold text-navy mb-1">Fotografia finale</p>
              <p className="text-body">La prova che siamo stati lì per te, visibile nella tua area riservata.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center pb-8">
        <Link to={cta} state={!user ? { from: `/checkout?type=singola${deep ? "&pulizia=specialistica" : ""}` } : undefined} data-testid="single-book-cta" className="btn-primary text-base !px-8 !py-3.5">
          {deep ? "Richiedi la pulizia profonda" : "Prenota la tua uscita"} <ArrowRight size={18} />
        </Link>
        {!user && <p className="text-xs text-muted-soft mt-3">Ti chiederemo di accedere o registrarti: serve un minuto.</p>}
      </div>
    </div>
  );
}
